// A project's home: live preview, publishing and billing, sharing, details, renders.
import { PLAN, tourUrl } from '@archviewvr/config';
import { qrSvg, qrPng } from '@archviewvr/engine';
import { getProject, getPanoramas, getLevels, draftUrl, startCheckout, openBilling, type Project, type Studio, unreadLeads } from '../data';
import { getWork } from '../process';
import { html, raw, icon, shell, formatBytes, formatLimit, toast, setShellProject, deviceSwitch, previewStage, wirePreview, $ } from '../ui';

const CHECKOUT_FLAG = 'checkout';

/** True when Stripe has just sent the studio back here after paying. */
function returnedFromCheckout(): boolean {
  return new URLSearchParams(location.search).get(CHECKOUT_FLAG) === 'done';
}
function clearCheckoutFlag(): void {
  const u = new URL(location.href);
  u.searchParams.delete(CHECKOUT_FLAG);
  history.replaceState(null, '', u.pathname + u.search + u.hash);
}

/** Waits for the Stripe webhook to mark the project published (usually a few seconds). */
async function waitForPublish(projectId: string, timeoutMs = 60000): Promise<Project | null> {
  const end = Date.now() + timeoutMs;
  while (Date.now() < end) {
    const p = await getProject(projectId);
    if (p?.state === 'published') return p;
    await new Promise((r) => setTimeout(r, 2000));
  }
  return null;
}

async function copy(text: string, what: string): Promise<void> {
  try { await navigator.clipboard.writeText(text); toast(`${what} copied`); }
  catch { toast('Couldn’t copy. Select the text and copy it instead.', 'error'); }
}

export async function projectView(root: HTMLElement, studio: Studio, projectId: string): Promise<void> {
  const page = shell(root, { studioName: studio.name, active: 'overview', project: { id: projectId } });
  page.innerHTML = '<div class="loading-page">Loading project</div>';
  let project = await getProject(projectId);
  if (!project) { page.innerHTML = html`<div class="empty"><h2>Project not found</h2><a class="btn" href="#/">Back to projects</a></div>`.html; return; }

  if (returnedFromCheckout()) {
    if (project.state !== 'published') {
      page.innerHTML = html`<div class="empty"><div class="pay-wait">${icon('spin', 28)}</div><h2>Finishing payment</h2><p class="muted">Publishing ${project.name}. This takes a few seconds.</p></div>`.html;
      const done = await waitForPublish(projectId);
      if (location.hash !== `#/p/${projectId}`) return; // they moved on
      clearCheckoutFlag();
      if (done) { project = done; toast(`${project.name} is live`); }
      else toast('Payment received. Publishing is taking longer than usual; refresh in a minute.', 'error', 9000);
    } else clearCheckoutFlag();
  }

  const p = project;
  setShellProject(root, p, () => openBilling(p.id), unreadLeads(p.id));
  const [panos, levels] = await Promise.all([getPanoramas(projectId), getLevels(projectId)]);
  const pending = getWork(projectId);
  const live = p.state === 'published';
  const freeUntil = live && p.free_until && new Date(p.free_until) > new Date() ? new Date(p.free_until).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
  const chipLabel = freeUntil ? `Free until ${freeUntil}` : { trial: 'Trial', published: 'Published', paused: 'Paused', archived: 'Archived' }[p.state];
  const link = tourUrl(p.slug);
  // drafts aren't counted in analytics; a published tour's draft shows without the watermark (and without its password)
  const previewUrl = live ? `/viewer/?draft=${p.id}&live=${p.slug}` : `/viewer/?draft=${p.id}`;
  const embed = `<iframe src="${link}" width="100%" height="600" style="border:0" allow="fullscreen; xr-spatial-tracking" allowfullscreen></iframe>`;
  const used = Math.min(100, (p.storage_bytes / p.storage_limit) * 100);

  // one Renders button: add or replace them, or adjust the saved ones without uploading
  const renders = panos.length
    ? html`<details class="menu-btn"><summary class="btn">Renders${icon('down', 14)}</summary>
        <div class="menu-pop">
          <a href="#/p/${p.id}/upload"><b>Add or replace renders</b><span>Drop the folder again. Same names replace the saved ones, new ones are added.</span></a>
          <a href="#/p/${p.id}/check"><b>Adjust scale and headings</b><span>Open the saved renders without uploading.</span></a>
        </div></details>`
    : html`<a class="btn" href="#/p/${p.id}/upload">Upload renders</a>`;
  const actions = live
    ? html`${renders}
        <a class="btn primary" href="${link}?studio=1" target="_blank" rel="noopener">${icon('eye', 16)}Open tour</a>`
    : html`${panos.length ? html`<a class="btn" href="${previewUrl}" target="_blank" rel="noopener">${icon('eye', 16)}Open preview</a>` : ''}
        ${renders}
        <button class="btn primary" id="publish" ${panos.length ? '' : raw('disabled title="Upload and save renders first"')}>${p.state === 'paused' ? 'Publish again' : 'Publish'} · $${PLAN.priceAud}/mo</button>`;

  const head = html`<div class="proj-head">
    <div class="l"><h1>${p.name}</h1><span class="chip ${p.state}"><span class="dot"></span>${chipLabel}</span></div>
    <div class="btn-row">${actions}</div>
  </div>`;

  const wire = () => {
    const pub = $('#publish', page) as HTMLButtonElement | null;
    if (pub) pub.onclick = async () => {
      pub.disabled = true; pub.textContent = 'Opening checkout…';
      try { await startCheckout(p.id); }
      catch (e) { toast(e instanceof Error ? e.message : 'Couldn’t open checkout', 'error', 8000); pub.disabled = false; pub.textContent = `Publish · $${PLAN.priceAud}/mo`; }
    };
    page.querySelectorAll<HTMLButtonElement>('[data-share]').forEach((b) => (b.onclick = () => {
      page.querySelectorAll<HTMLButtonElement>('[data-share]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      page.querySelectorAll<HTMLElement>('[data-sharepane]').forEach((x) => (x.hidden = x.dataset.sharepane !== b.dataset.share));
    }));
    // the Renders menu shuts when you click elsewhere
    const menu = page.querySelector<HTMLDetailsElement>('.menu-btn');
    if (menu) { const shut = (e: MouseEvent) => { if (menu.open && !menu.contains(e.target as Node)) menu.open = false; }; document.addEventListener('click', shut); addEventListener('hashchange', () => document.removeEventListener('click', shut), { once: true }); }
    const cl = $('#copy-link', page); if (cl) cl.onclick = () => copy(link, 'Link');
    const ce = $('#copy-embed', page); if (ce) ce.onclick = () => copy(embed, 'Embed code');
    const ck = $('#copy-kiosk', page); if (ck) ck.onclick = () => copy(`${link}?kiosk=1`, 'Kiosk link');
    const qd = $('#qr-dl', page); if (qd) qd.onclick = async () => {
      const a = document.createElement('a'); a.href = URL.createObjectURL(await qrPng(link, 1200));
      a.download = `${p.name.replace(/[^\w -]/g, '').trim() || 'tour'} QR code.png`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    };
  };

  if (!panos.length) {
    page.innerHTML = html`${head}
      <div class="empty"><h2>${pending?.done ? 'Your renders are checked but not saved yet' : 'No renders yet'}</h2>
        <p class="muted">${pending?.done ? 'Finish the check step to save them.' : 'Drop the folder from 3ds Max: model, camera points and renders.'}</p>
        <a class="btn primary" href="#/p/${p.id}/${pending?.done ? 'check' : 'upload'}">${pending?.done ? 'Continue to check' : 'Upload renders'}</a></div>`.html;
    wire();
    return;
  }

  const tiles = panos.map((r) => html`<figure class="tile"><div class="img"><img src="${draftUrl(p.id, `thumbs/${r.id}.jpg`)}" alt="" loading="lazy"></div>
    ${r.check_state === 'ok' ? '' : html`<span class="mark warn" title="Flagged at check">!</span>`}
    <figcaption><span>${r.name}</span><em>${levels.find((l) => l.id === r.level_id)?.name ?? 'Between levels'}</em></figcaption></figure>`);

  const share = live
    ? html`<div class="box share"><div class="share-head"><h2>Share the tour</h2>
          <div class="seg small" role="tablist" aria-label="Share"><button data-share="link" aria-pressed="true">Link</button><button data-share="embed">Embed</button><button data-share="kiosk">Kiosk</button><button data-share="qr">QR code</button></div></div>
        <div data-sharepane="link"><div class="copy-row"><input readonly value="${link}" aria-label="Tour link"><button class="btn" id="copy-link">Copy</button></div>
          <p class="muted small">Send it to buyers or put it in a listing.</p></div>
        <div data-sharepane="embed" hidden><div class="copy-row"><input readonly value="${embed}" aria-label="Embed code"><button class="btn" id="copy-embed">Copy</button></div>
          <p class="muted small">Paste it into a page of your website to show the tour there.</p></div>
        <div data-sharepane="kiosk" hidden><div class="copy-row"><input readonly value="${link}?kiosk=1" aria-label="Kiosk link"><button class="btn" id="copy-kiosk">Copy</button></div>
          <p class="muted small">For a display suite screen: it plays the tour by itself when nobody is touching it. Timings are in <a href="#/p/${p.id}/brand/extras">Brand kit</a>.</p></div>
        <div data-sharepane="qr" hidden><div class="share-qr-row"><div class="qr-box">${raw(qrSvg(link, { margin: 1, title: 'QR code for the tour link' }))}</div>
          <div class="share-qr-r"><button class="btn" id="qr-dl">${icon('download', 16)}Download PNG</button><span class="muted small">For brochures and signage.</span></div></div></div>
      </div>`
    : p.state === 'paused'
      ? html`<div class="box notice"><strong>This tour is paused.</strong> Its subscription ended, so the link shows “not available”. Your renders are kept. Publish again to bring the same link back.</div>`
      : html`<div class="box notice"><strong>Trial preview.</strong> Only you can see it, and it carries a watermark. Publish for $${PLAN.priceAud} AUD a month to get a share link, remove the watermark and upload every render. Cancel any time.</div>`;

  page.innerHTML = html`${head}
    <div class="ov-grid">
      <div class="ov-side">
        ${share}
      <div class="box"><h2 style="margin-bottom:14px">Details</h2>
        <dl class="kv">
          <dt>Renders</dt><dd>${panos.length}${p.state === 'trial' ? ` of ${PLAN.trialPanoLimit} (trial)` : ''}</dd>
          <dt>Levels</dt><dd>${levels.map((l) => `${l.name} · ${l.height.toFixed(1)} m`).join(', ') || '—'}</dd>
          <dt>Client</dt><dd>${p.client_name || '—'}</dd>
          <dt>Tour link</dt><dd>${live ? link.replace(/^https?:\/\//, '') : 'Available once published'}</dd>
          ${freeUntil ? html`<dt>Billing</dt><dd>Free until ${freeUntil}. Add a card under Billing to keep it live after that.</dd>` : ''}
          ${live && p.published_at ? html`<dt>Published</dt><dd>${new Date(p.published_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</dd>` : ''}
        </dl>
      </div>
      <div class="box storage"><div class="top"><span>Storage</span><span class="mono">${formatBytes(p.storage_bytes)} / ${formatLimit(p.storage_limit)}</span></div>
        <div class="bar"><i style="width:${used.toFixed(1)}%"></i></div>
        <p class="muted" style="font-size:12.5px">${p.state === 'trial' ? `Trial projects keep ${PLAN.trialPanoLimit} renders and a private preview with a watermark. Publishing unlocks all renders and a share link.` : 'Includes every render size and the model.'}</p>
        <a class="btn small" href="mailto:${PLAN.supportEmail}?subject=${encodeURIComponent(`More storage for ${p.name ?? 'my project'}`)}&amp;body=${encodeURIComponent(`Hi, I'd like more storage for ${p.name ?? 'my project'} (${p.id}).`)}">Run out of space? Contact us</a>
      </div>
      </div>
      <div class="ov-preview">
        <div class="pv-bar-top"><span class="muted small">Preview</span>${deviceSwitch()}</div>
        ${previewStage('pv-frame', previewUrl)}
      </div>
    </div>
    <section style="display:grid;gap:14px"><h2>Renders</h2><div class="thumbs">${tiles}</div></section>`.html;
  wire();
  wirePreview($('.ov-preview', page));
}
